import {nextTick} from 'vue';

const JOY_FORM_FIELDS = ['.wysiwyg-editor_root', '[data-joy-form-field]', /* PhoneNumberV2 component */ '.vti__input'];

const focusableElements = ['input', 'textarea', '.wysiwyg-editor__content', /* PhoneNumberV2 component */ '.vti__input'];

/**
 * Find the closest sibling of the element that matches the selector
 * @param element - The element to find the closest sibling of
 * @param selector - The selector to match the sibling
 * @returns The closest sibling of the element that matches the selector
 */
export function closestSibling(element: Element, selector: string): Element | undefined {
    let sibling = element.previousElementSibling;

    while (sibling) {
        if (sibling.matches(selector)) return sibling;
        else if (sibling.querySelector(selector)) return sibling.querySelector(selector) as Element;
        sibling = sibling.previousElementSibling;
    }

    if (element.parentElement) {
        return closestSibling(element.parentElement, selector);
    }

    return undefined; // no matching sibling found
}

/**
 * Whether an error node is actually shown to the user (not `display: none`, `visibility: hidden`, `opacity: 0`, etc.,
 * including on ancestors — same cases as `v-show="false"`).
 */
function isJoyFormErrorVisible(element: Element): boolean {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    // Safari 17.4 +
    if (typeof element.checkVisibility === 'function') {
        try {
            return element.checkVisibility({
                checkOpacity: true,
                checkVisibilityCSS: true,
                contentVisibilityAuto: true,
            });
        } catch {
            // Fall through to computed-style walk
        }
    }

    let current: HTMLElement | null = element;
    while (current) {
        const style = getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
            return false;
        }
        if (parseFloat(style.opacity) === 0) {
            return false;
        }
        current = current.parentElement;
    }

    return true;
}

export interface ScrollToErrorOptions {
    /**
     * The id of the form to scroll to the first error. This prevents the method to focus the wrong form if multiple forms are present
     *
     * If not provided, the body will be used.
     */
    formId?: string;
    /** Scroll behavior */
    behavior?: 'smooth' | 'auto';
    /** Scroll block */
    block?: 'start' | 'center' | 'end';
    /** Debug mode (log errors and elements to scroll to) */
    debug?: boolean;
}

/**
 * Scroll to the first error found in the DOM or of the form container if provided
 * @param [options] - The options for the scroll to error.
 * @returns False if the form has errors, true otherwise
 *
 * @example
 * ```ts
 * const valid = await scrollToError();
 * // Or
 * const valid = await scrollToError({formId: 'my-form-id'});
 * ```
 */
export async function scrollToError(options?: ScrollToErrorOptions): Promise<boolean> {
    const {formId, behavior = 'smooth', block = 'center', debug = false} = options ?? {};

    const rootElement = formId ? document.getElementById(formId) : document.body;
    if (!rootElement) {
        if (debug || import.meta.dev) {
            // eslint-disable-next-line no-console
            console.log('No root element found for formId: ', formId);
        }
        return false;
    }

    await nextTick();
    const errorElements = Array.from(rootElement.querySelectorAll('.joy-form-error, [data-joy-form-error], joy-form-error'));
    const visibleErrorElements = errorElements.filter(isJoyFormErrorVisible);

    if (debug || import.meta.dev) {
        // eslint-disable-next-line no-console
        console.log('errorElements: ', errorElements);
        // eslint-disable-next-line no-console
        console.log('visibleErrorElements: ', visibleErrorElements);
    }

    if (visibleErrorElements.length) {
        const elementsToFind = JOY_FORM_FIELDS.join(', ');
        let elementToScrollTo =
            (((visibleErrorElements[0] && closestSibling(visibleErrorElements[0], elementsToFind)) ??
                visibleErrorElements[0]?.closest(elementsToFind)) as HTMLElement) ?? visibleErrorElements[0];

        if (!rootElement.contains(elementToScrollTo)) {
            elementToScrollTo = visibleErrorElements[0] as HTMLElement;
        }

        if (debug || import.meta.dev) {
            // eslint-disable-next-line no-console
            console.log('elementToScrollTo: ', elementToScrollTo);
        }

        if (elementToScrollTo) {
            elementToScrollTo.scrollIntoView({behavior, block});

            const focusableElementsToFind = focusableElements.join(', ');
            let elementToFocusTo: HTMLElement | null = null;

            if (elementToScrollTo.matches(focusableElementsToFind)) {
                elementToFocusTo = elementToScrollTo;
            } else if (elementToScrollTo.querySelector(focusableElementsToFind)) {
                elementToFocusTo = elementToScrollTo.querySelector(focusableElementsToFind) as HTMLElement;
            } else {
                elementToFocusTo = (closestSibling(elementToScrollTo, focusableElementsToFind) ??
                    elementToScrollTo.closest(focusableElementsToFind)) as HTMLElement | null;
            }

            if (debug || import.meta.dev) {
                // eslint-disable-next-line no-console
                console.log('elementToFocusTo: ', elementToFocusTo);
            }

            if (elementToFocusTo && 'focus' in elementToFocusTo) {
                elementToFocusTo.focus({preventScroll: true});
            }
            return false;
        } else {
            return true;
        }
    }
    return true;
}
